/**
 * CoachPage — conversational entry point at /coach.
 *
 * Two-column layout (md+): chat on the logical start side, live
 * preview on the end. Mobile collapses to a single column with a
 * tab switcher — the preview only becomes useful once the draft is
 * under way, so the default tab is chat.
 *
 * State lives on the Zustand coach store; this component is the
 * orchestrator that translates Coach replies into committed state
 * and drives the handoff into Spark Step 6.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';
import { coach as coachApi } from '@/api/endpoints/coach';
import { cn } from '@/utils/cn';

import { ChatPanel } from './ChatPanel';
import { CoachContextSidebar } from './CoachContextSidebar';
import { LivePreview } from './LivePreview';
import { applyDraftToSpark } from './handoff';
import { jumpToDomain, type CoachReplyPatch, type CoachState } from './engine';
import { canHandoff, useCoachStore, type CoachMessage } from './store';
import type { CoachDomain } from './trees';

type Tab = 'chat' | 'preview';

export function CoachPage() {
  const { t } = useTranslation('coach');
  const navigate = useNavigate();

  const messages = useCoachStore((s) => s.messages);
  const engine = useCoachStore((s) => s.engine);
  const draft = useCoachStore((s) => s.draft);
  const thinking = useCoachStore((s) => s.thinking);
  const appendMessage = useCoachStore((s) => s.appendMessage);
  const setEngine = useCoachStore((s) => s.setEngine);
  const setDraft = useCoachStore((s) => s.setDraft);
  const setThinking = useCoachStore((s) => s.setThinking);
  const reset = useCoachStore((s) => s.reset);

  const [tab, setTab] = useState<Tab>('chat');

  // Seed the very first Coach greeting on mount if the transcript is
  // empty. A page reload wipes the store so users get the greeting
  // fresh each visit (by design — the draft is the artifact).
  useEffect(() => {
    if (messages.length === 0) {
      appendMessage(makeCoachMessage(t('engine.greeting')));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const askMutation = useMutation({
    mutationFn: ({ state, text }: { state: CoachState; text: string }) =>
      coachApi.ask(state, text),
    onSuccess: (patch) => {
      applyPatch(patch);
    },
    onError: () => {
      appendMessage(makeCoachMessage(t('engine.internal_error')));
      setThinking(false);
    },
  });

  function applyPatch(patch: CoachReplyPatch) {
    const tFn: TFn = (k) => t(k);
    appendMessage(makeCoachMessage(renderReply(patch, tFn)));
    setEngine(patch.next);
    if (patch.draft) setDraft(patch.draft);
    setThinking(false);
  }

  function handleSend(text: string) {
    appendMessage(makeUserMessage(text));
    setThinking(true);
    askMutation.mutate({ state: engine, text });
  }

  function handlePickDomain(id: CoachDomain) {
    // Chips stand in for the user's first message — render the
    // localized chip label as a user bubble so the transcript reads
    // coherently.
    appendMessage(makeUserMessage(t(`quickstart.${id}`)));
    // Direct state jump — no mock-latency wrapper needed since no AI
    // call actually happens for the chip.
    const patch = jumpToDomain(id);
    applyPatch(patch);
  }

  function handleReset() {
    reset();
    setTab('chat');
    // Re-seed greeting for the fresh transcript.
    queueMicrotask(() => {
      appendMessage(makeCoachMessage(t('engine.greeting')));
    });
  }

  function handleConfirm() {
    if (!draft) return;
    applyDraftToSpark(draft);
    navigate('/spark');
  }

  const ready = canHandoff(engine, draft);

  return (
    <div className="flex flex-col gap-6">
      <Heading onReset={handleReset} />

      <CoachContextSidebar />

      {/* Mobile tabs */}
      <div className="md:hidden">
        <div
          role="tablist"
          aria-label={t('page.tabs_label')}
          className="inline-flex rounded-md border border-border bg-surface p-1"
        >
          <TabButton active={tab === 'chat'} onClick={() => setTab('chat')}>
            {t('page.tab_chat')}
          </TabButton>
          <TabButton active={tab === 'preview'} onClick={() => setTab('preview')}>
            {t('page.tab_preview')}
            {draft ? (
              <span className="ms-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-spark px-1 text-[10px] font-semibold text-white">
                1
              </span>
            ) : null}
          </TabButton>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
        <section
          aria-label={t('page.tab_chat')}
          className={cn(
            'min-h-[480px]',
            tab === 'chat' ? 'block' : 'hidden md:block',
          )}
        >
          <ChatPanel
            messages={messages}
            engine={engine}
            thinking={thinking}
            onSend={handleSend}
            onPickDomain={handlePickDomain}
          />
        </section>

        <section
          aria-label={t('page.tab_preview')}
          className={cn(
            'min-h-[320px]',
            tab === 'preview' ? 'block' : 'hidden md:block',
          )}
        >
          <LivePreview
            draft={draft}
            ready={ready}
            onConfirm={handleConfirm}
            onReset={handleReset}
          />
        </section>
      </div>
    </div>
  );
}

function Heading({ onReset }: { onReset: () => void }) {
  const { t } = useTranslation('coach');
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-ink">{t('page.title')}</h1>
        <p className="mt-1 text-sm text-ink-muted">{t('page.subtitle')}</p>
      </div>
      <Button variant="ghost" size="sm" onClick={onReset}>
        {t('page.reset')}
      </Button>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        'inline-flex items-center rounded px-3 py-1.5 text-sm font-medium transition-colors',
        active ? 'bg-spark text-white' : 'text-ink-muted hover:text-ink',
      )}
    >
      {children}
    </button>
  );
}

// ─── Message helpers ─────────────────────────────────────────────

function makeUserMessage(body: string): CoachMessage {
  return {
    id: crypto.randomUUID(),
    author: 'user',
    body,
    at: new Date().toISOString(),
  };
}

function makeCoachMessage(body: string): CoachMessage {
  return {
    id: crypto.randomUUID(),
    author: 'coach',
    body,
    at: new Date().toISOString(),
  };
}

// ─── Reply rendering ──────────────────────────────────────────────
//
// i18next's generic `TFunction<'coach'>` narrows keys to the compile-time
// keyset of the namespace. Our patch carries dynamic `reply_key` strings
// derived at runtime from the tree lookup, so a thin wrapper keeps the
// call sites readable without fighting the generic.

type TFn = (key: string) => string;

function renderReply(patch: CoachReplyPatch, t: TFn): string {
  const params = patch.reply_params ?? {};

  switch (patch.reply_key) {
    case 'engine.ack_then_q1': {
      const ack = t(String(params.ack_key));
      const q1 = t(String(params.q1_key));
      return `${ack}\n\n${q1}`;
    }
    case 'engine.ask_q2': {
      return t(String(params.q2_key));
    }
    case 'engine.ready': {
      return t(String(params.ready_key));
    }
    case 'engine.raw': {
      // Real-mode Coach: the server streamed free-form Claude text
      // that does not map to an i18n key. Passed through verbatim.
      return String(params.text ?? '');
    }
    default:
      return t(patch.reply_key);
  }
}

export default CoachPage;
